import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EASE } from "@/lib/motion";
import { Reveal } from "@/components/Reveal";
import { SectionHeading, CornerTicks } from "@/components/kit";

const STAGES = [
    { id: "task", num: "01", name: "TASK", desc: "A user defines an objective. The task is specified with reward, capability requirements, evidence requirements and a deadline.", input: "USER OBJECTIVE", output: "TASK SPEC" },
    { id: "agent", num: "02", name: "AGENT", desc: "An autonomous entity — AI agent, robot, drone, software agent or machine — accepts the task and signs the acceptance.", input: "TASK SPEC", output: "ACCEPTANCE" },
    { id: "action", num: "03", name: "ACTION", desc: "The agent performs the task in the real world or on the network. Execution begins generating raw activity.", input: "ACCEPTANCE", output: "EXECUTION" },
    { id: "evidence", num: "04", name: "EVIDENCE", desc: "Execution generates evidence — GPS, sensors, images, logs, traces, signatures, timestamps — depending on the task.", input: "EXECUTION", output: "EVIDENCE PACKAGE" },
    { id: "verification", num: "05", name: "VERIFICATION", desc: "Evidence is evaluated by the verification engine and verifier pool against the task's verification method.", input: "EVIDENCE PACKAGE", output: "VERDICT" },
    { id: "proof", num: "06", name: "PROOF", desc: "A verified action becomes a Proof of Action — a durable, inspectable record of what actually happened.", input: "VERDICT", output: "ACTION PROOF" },
    { id: "reputation", num: "07", name: "REPUTATION", desc: "The agent's history is updated. Reputation emerges from verified action over time — not from claims.", input: "ACTION PROOF", output: "SCORE UPDATE" },
    { id: "settlement", num: "08", name: "SETTLEMENT", desc: "Verified outcomes can trigger payment in USDC, natively settled on Arc.", input: "ACTION PROOF", output: "USDC RELEASE" },
];

export const ActionFlow = () => {
    const [active, setActive] = useState(0);
    const stage = STAGES[active];

    return (
        <section id="action-flow" data-testid="action-flow" className="section relative">
            <div className="shell">
                <Reveal>
                    <SectionHeading
                        index="02"
                        eyebrow="CORE ACTION FLOW"
                        title={
                            <>
                                EIGHT STAGES.
                                <br />
                                ONE <span className="text-[#1677FF]">VERIFIED ACTION.</span>
                            </>
                        }
                        description="Every action that moves through ACTRA follows the same path. Select a stage to inspect it."
                    />
                </Reveal>

                <Reveal delay={0.1} className="mt-14">
                    <div className="relative" data-testid="action-flow-nodes">
                        <div className="absolute left-0 right-0 top-5 hidden h-px bg-white/[0.08] lg:block" aria-hidden="true" />
                        <div className="grid grid-cols-2 gap-y-6 sm:grid-cols-4 lg:grid-cols-8">
                            {STAGES.map((s, i) => (
                                <button
                                    key={s.id}
                                    data-testid={`action-node-${s.id}`}
                                    onClick={() => setActive(i)}
                                    aria-pressed={active === i}
                                    className="group relative flex flex-col items-center gap-3 text-center"
                                >
                                    <span
                                        className={`relative z-10 flex h-10 w-10 items-center justify-center border font-mono text-[10px] transition-all duration-300 ${
                                            active === i
                                                ? "border-[#1677FF] bg-[#1677FF] text-white shadow-[0_0_24px_rgba(22,119,255,0.5)]"
                                                : "border-white/15 bg-[#0A1627] text-mute group-hover:border-[#1677FF]/60 group-hover:text-ink"
                                        }`}
                                    >
                                        {s.num}
                                    </span>
                                    <span
                                        className={`font-mono text-[10px] uppercase tracking-[0.24em] transition-colors duration-300 ${
                                            active === i ? "text-ink" : "text-mute group-hover:text-ink"
                                        }`}
                                    >
                                        {s.name}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="relative mt-12">
                        <CornerTicks />
                        <div className="border border-white/[0.08] bg-[#07111F]/80 p-7 md:p-10">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={stage.id}
                                    data-testid={`action-flow-panel-${stage.id}`}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -14 }}
                                    transition={{ duration: 0.35, ease: EASE }}
                                    className="grid gap-8 md:grid-cols-[1fr_auto] md:items-start"
                                >
                                    <div>
                                        <p className="label-tech">STAGE {stage.num}</p>
                                        <h3 className="mt-2 font-display text-3xl font-bold uppercase tracking-tightest text-ink md:text-4xl">
                                            {stage.name}
                                        </h3>
                                        <p className="mt-4 max-w-2xl leading-relaxed text-mute">{stage.desc}</p>
                                    </div>
                                    <div className="flex flex-col gap-3 md:w-56">
                                        <div className="border border-white/[0.08] bg-[#0A1627] p-4">
                                            <p className="label-tech">INPUT</p>
                                            <p className="mt-1 font-mono text-xs text-ink">{stage.input}</p>
                                        </div>
                                        <div className="flex justify-center">
                                            <span className="h-4 w-px bg-[#1677FF]/50" aria-hidden="true" />
                                        </div>
                                        <div className="border border-[#1677FF]/30 bg-[#1677FF]/[0.06] p-4">
                                            <p className="label-tech">OUTPUT</p>
                                            <p className="mt-1 font-mono text-xs text-[#31D8FF]">{stage.output}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default ActionFlow;
